import { cn } from '@/lib/cn'

/**
 * Shared class recipes. Kept out of component files so links, buttons and native
 * controls can look identical without one importing the other.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle' | 'link'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon'

const BUTTON_BASE =
  'relative inline-flex items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap ' +
  'transition-colors duration-100 select-none ' +
  'disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50'

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-2.5 text-[0.8125rem]',
  md: 'h-9 px-3.5',
  lg: 'h-10 px-4 text-[0.9375rem]',
  icon: 'size-9 shrink-0 p-0',
}

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white shadow-card hover:bg-brand-700 active:bg-brand-800',
  secondary:
    'border border-line-strong bg-surface text-ink shadow-card hover:bg-surface-sunken active:bg-surface-sunken',
  ghost: 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
  danger: 'bg-danger-600 text-white shadow-card hover:bg-danger-700 active:bg-danger-700',
  subtle: 'bg-brand-50 text-brand-700 hover:bg-brand-100',
  link: 'h-auto px-0 text-brand-700 underline underline-offset-2 hover:text-brand-800',
}

export function buttonStyles(
  options: {
    variant?: ButtonVariant
    size?: ButtonSize
    fullWidth?: boolean
    className?: string
  } = {},
): string {
  const { variant = 'primary', size = 'md', fullWidth = false, className } = options
  return cn(
    BUTTON_BASE,
    BUTTON_SIZES[size],
    BUTTON_VARIANTS[variant],
    fullWidth && 'w-full',
    className,
  )
}

/** Text inputs, textareas and selects share one skin so a form reads as one row of controls. */
export function controlStyles(options: { invalid?: boolean; className?: string } = {}): string {
  const { invalid = false, className } = options
  return cn(
    'block w-full rounded-md border bg-surface px-2.5 py-1.5 text-ink shadow-card',
    'transition-colors placeholder:text-ink-subtle',
    'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-subtle',
    'read-only:bg-surface-sunken',
    invalid
      ? 'border-danger-500 focus-visible:outline-danger-600'
      : 'border-line-strong hover:border-ink-subtle',
    className,
  )
}
